/**
 * SubscriptionsPage · iter136 UI
 * Abos mit Lifecycle-Badge (ACTIVE/PAYMENT_RETRY/GRACE/CANCELLED_EOT/EXPIRED)
 * + "Kündigen zum Perioden-Ende"-Button für aktive Abos.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserPlus, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_META = {
  ACTIVE: { icon: CheckCircle2, chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Aktiv" },
  PAYMENT_RETRY: { icon: AlertTriangle, chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Zahlung wird wiederholt" },
  GRACE_PERIOD: { icon: Clock, chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Kulanzfrist läuft" },
  CANCELLED_END_OF_TERM: { icon: CalendarClock, chip: "bg-slate-50 text-slate-700 border-slate-200", label: "Endet zum Periodenende" },
  EXPIRED: { icon: XCircle, chip: "bg-red-50 text-red-700 border-red-200", label: "Abgelaufen" },
  CANCELLED: { icon: XCircle, chip: "bg-red-50 text-red-700 border-red-200", label: "Gekündigt" },
  PENDING: { icon: Clock, chip: "bg-slate-50 text-slate-500 border-slate-200", label: "In Verarbeitung" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; }
}

export default function SubscriptionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const { data } = await api.get("/me/subscriptions");
      setSubs(data?.subscriptions || data || []);
    } catch { setSubs([]); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  const cancelEOT = async (sub) => {
    if (!window.confirm(t("subscriptions.confirm_cancel", {
      defaultValue: "Wirklich zum Ende der laufenden Periode kündigen? Dein Zugang bleibt bis dahin aktiv.",
    }))) return;
    setCancellingId(sub.subscription_id);
    try {
      await api.post(`/subscriptions/${sub.subscription_id}/cancel-end-of-term`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || t("subscriptions.cancel_error", { defaultValue: "Kündigung konnte nicht verarbeitet werden." }));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div data-testid="subscriptions-page" className="w-full max-w-3xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0 py-6">
      <h1 className="text-2xl font-bold text-[#0A0A0A] px-4 mb-1 flex items-center gap-2">
        <UserPlus size={22} /> {t("subscriptions.title", { defaultValue: "Abonnements" })}
      </h1>
      <p className="text-sm text-[#666] px-4 mb-6">{t("subscriptions.subtitle", { defaultValue: "Deine Creator-Abos" })}</p>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#999]" /></div>
      ) : subs.length === 0 ? (
        <div data-testid="subs-empty" className="text-center py-16">
          <UserPlus size={40} className="mx-auto text-[#E5E5E5] mb-3" />
          <p className="text-sm text-[#999]">{t("subscriptions.empty", { defaultValue: "Noch keine aktiven Abos." })}</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3 px-4">
          {subs.map((s) => {
            const rawStatus = String(s.status || "ACTIVE").toUpperCase();
            const meta = STATUS_META[rawStatus] || STATUS_META.ACTIVE;
            const StatusIcon = meta.icon;
            const creator = s.creator || s.creator_profile || {};
            const canCancel = ["ACTIVE", "PAYMENT_RETRY", "GRACE_PERIOD"].includes(rawStatus);
            const showAccessUntil = ["CANCELLED_END_OF_TERM", "GRACE_PERIOD"].includes(rawStatus);
            const showRetryWarning = rawStatus === "PAYMENT_RETRY";
            const showGraceWarning = rawStatus === "GRACE_PERIOD";
            return (
              <li key={s.subscription_id || s.id}
                  data-testid={`sub-item-${s.subscription_id || s.id}`}
                  className="border border-[#E5E5E5] rounded-2xl p-3 bg-white">
                <button
                  data-testid={`sub-open-creator-${s.subscription_id || s.id}`}
                  onClick={() => creator.handle && navigate(`/user/${creator.handle}`)}
                  className="w-full flex items-center gap-3 text-left hover:opacity-90">
                  <div className="w-12 h-12 rounded-full bg-[#E5E5E5] overflow-hidden shrink-0">
                    {creator.avatar_url && <img src={creator.avatar_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0A0A0A] truncate">
                      {creator.display_name || creator.handle || t("subscriptions.unknown", { defaultValue: "Unbekannt" })}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {s.tier && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5F5F5] border border-[#E5E5E5] text-[#666] uppercase tracking-wider font-semibold">{s.tier}</span>}
                      <span
                        data-testid={`sub-status-${s.subscription_id || s.id}`}
                        className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold inline-flex items-center gap-1 ${meta.chip}`}>
                        <StatusIcon size={10} /> {meta.label}
                      </span>
                    </div>
                    {s.next_renewal_at && rawStatus === "ACTIVE" && (
                      <p className="text-xs text-[#999] mt-1">
                        {t("subscriptions.next_renewal", { defaultValue: "Nächste Verlängerung" })}: {fmtDate(s.next_renewal_at)}
                      </p>
                    )}
                    {showAccessUntil && (s.current_period_end || s.grace_period_ends_at) && (
                      <p data-testid={`sub-access-until-${s.subscription_id || s.id}`} className="text-xs text-[#666] mt-1">
                        {t("subscriptions.access_until", { defaultValue: "Zugang bis" })}: <span className="font-semibold">{fmtDate(s.grace_period_ends_at || s.current_period_end)}</span>
                      </p>
                    )}
                  </div>
                  {s.price_eur != null && <span className="text-sm font-bold text-[#0A0A0A]">{Number(s.price_eur).toFixed(2)} €</span>}
                </button>

                {/* iter136: Statusabhängige Hinweisboxen */}
                {showRetryWarning && (
                  <div data-testid={`sub-warn-retry-${s.subscription_id || s.id}`} className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <p>{t("subscriptions.warn_retry", { defaultValue: "Deine letzte Zahlung ist fehlgeschlagen. Wir versuchen es automatisch nochmal. Aktualisiere ggf. deine Zahlungsmethode." })}</p>
                  </div>
                )}
                {showGraceWarning && (
                  <div data-testid={`sub-warn-grace-${s.subscription_id || s.id}`} className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-start gap-2">
                    <Clock size={14} className="mt-0.5 shrink-0" />
                    <p>{t("subscriptions.warn_grace", { defaultValue: "Dein Abo befindet sich in der Kulanzfrist. Nach Ablauf erlischt der Zugang." })}</p>
                  </div>
                )}

                {/* Cancel-EOT-Button */}
                {canCancel && rawStatus !== "CANCELLED_END_OF_TERM" && (
                  <button
                    data-testid={`sub-cancel-eot-${s.subscription_id || s.id}`}
                    onClick={() => cancelEOT(s)}
                    disabled={cancellingId === s.subscription_id}
                    className="mt-3 w-full h-9 rounded-full border border-[#E5E5E5] text-xs font-semibold text-[#666] hover:bg-[#F5F5F5] disabled:opacity-40 flex items-center justify-center gap-2">
                    {cancellingId === s.subscription_id ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />}
                    {t("subscriptions.cancel_eot", { defaultValue: "Zum Periodenende kündigen" })}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
